import axios from "axios";
import httpClient, { initializeCustomDomain } from "../../clients/httpClient";
import { appendToFormData } from "../../helpers/formdata-helper";
import {
    AnnouncementInteraction,
    IAnnouncement,
    IAnnouncementListQuery,
    IAnnouncementMetrics,
    IAnnouncementOptions,
    IAnnouncementPayload,
    IAnnouncementsResponse,
} from "../../interfaces/announcement.interface";

const ADMIN_PATH = "/api/v1/admin/announcements";
const PLATFORM_PATH = "/api/v1/announcements";

// The builder endpoints go through httpClient on purpose — its error toasts
// (validation, publish race, 403) are exactly what the author needs to see.

// The httpClient response interceptor already returns `response.data`, so the
// declared return types below are the envelope the caller actually receives —
// no second `.data` hop at the call site.
export const getAnnouncementsRequest = (
    query: IAnnouncementListQuery
): Promise<IAnnouncementsResponse> =>
    httpClient.get(ADMIN_PATH, {
        params: {
            status: query.status || undefined,
            message_type: query.messageType || undefined,
            search: query.search || undefined,
            per_page: query.perPage,
            page: query.page,
        },
    }) as unknown as Promise<IAnnouncementsResponse>;

export const getAnnouncementOptionsRequest = (): Promise<{
    data: IAnnouncementOptions;
}> =>
    httpClient.get(`${ADMIN_PATH}/options`) as unknown as Promise<{
        data: IAnnouncementOptions;
    }>;

export const getAnnouncementRequest = (
    id: string | number
): Promise<{ data: IAnnouncement }> =>
    httpClient.get(`${ADMIN_PATH}/${id}`) as unknown as Promise<{
        data: IAnnouncement;
    }>;

// A picked file forces multipart; everything else stays JSON so that
// `image: null` (remove the image) survives as a real null.
const buildFormData = (payload: IAnnouncementPayload) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([field, value]) => {
        if (value === undefined) return;
        appendToFormData(formData, field, value);
    });
    return formData;
};

export const createAnnouncementRequest = (
    payload: IAnnouncementPayload
): Promise<{ data: IAnnouncement }> =>
    (payload.image instanceof File
        ? httpClient.post(ADMIN_PATH, buildFormData(payload))
        : httpClient.post(ADMIN_PATH, payload)) as unknown as Promise<{
        data: IAnnouncement;
    }>;

export const updateAnnouncementRequest = (
    id: string | number,
    payload: IAnnouncementPayload
): Promise<{ data: IAnnouncement }> =>
    (payload.image instanceof File
        ? httpClient.post(`${ADMIN_PATH}/${id}`, buildFormData(payload), {
              params: { _method: "PATCH" },
          })
        : httpClient.patch(
              `${ADMIN_PATH}/${id}`,
              payload
          )) as unknown as Promise<{ data: IAnnouncement }>;

// Publishing demotes whatever was live, so every caller must refetch the list.
export const publishAnnouncementRequest = (id: string | number) =>
    httpClient.post(`${ADMIN_PATH}/${id}/publish`);

export const unpublishAnnouncementRequest = (id: string | number) =>
    httpClient.post(`${ADMIN_PATH}/${id}/unpublish`);

export const getAnnouncementMetricsRequest = (
    id: string | number
): Promise<{ data: IAnnouncementMetrics }> =>
    httpClient.get(`${ADMIN_PATH}/${id}/metrics`) as unknown as Promise<{
        data: IAnnouncementMetrics;
    }>;

export const deleteAnnouncementRequest = (id: string | number) =>
    httpClient.delete(`${ADMIN_PATH}/${id}`);

// The two platform-modal calls below deliberately bypass httpClient:
//  1. its response interceptor toasts every non-2xx, and a 422 on /interact
//     only means "the announcement is no longer live" — the user must not see it;
//  2. it dedupes concurrent requests to the same URL by aborting the earlier
//     one, and an aborted request resolves to a never-settling promise. This
//     fetch is layout-mounted, so it must stay out of that trap.
// Mirrors fetchProjectAttributesRaw in organization-user/organization.service.ts.
const resolveBaseUrl = async (): Promise<string> => {
    let org = localStorage.getItem("org");
    if (!org) {
        await initializeCustomDomain();
        org = localStorage.getItem("org");
    }
    const protocol = import.meta.env.VITE_BACKEND_PROTOCOL ?? "https";
    return org
        ? `${protocol}://${org}.${import.meta.env.VITE_ORG_API_ENDPOINT}`
        : import.meta.env.VITE_API_ENDPOINT;
};

const authHeaders = () => {
    const token = JSON.parse(localStorage.getItem("token") || "{}")
        ?.access_token;
    return {
        Authorization: token ? `Bearer ${token}` : "",
        Accept: "application/json",
    };
};

// Raw axios, so `response.data` is the untouched envelope and the resource
// really does sit at `response.data.data`. Resolves null when the user has
// already interacted or nothing is published.
export const getCurrentAnnouncement =
    async (): Promise<IAnnouncement | null> => {
        try {
            const baseURL = await resolveBaseUrl();
            const response = await axios.get(
                `${baseURL}${PLATFORM_PATH}/current`,
                { headers: authHeaders() }
            );
            return response.data?.data ?? null;
        } catch {
            return null;
        }
    };

// Fire-and-forget and idempotent server-side: `click` and `dismiss` backfill
// `view`, repeats keep the original timestamp, and a 422 just means the
// announcement is no longer live. Never surfaces an error.
export const recordAnnouncementInteraction = async (
    id: number,
    action: AnnouncementInteraction
): Promise<void> => {
    try {
        const baseURL = await resolveBaseUrl();
        await axios.post(
            `${baseURL}${PLATFORM_PATH}/${id}/interact`,
            { action },
            { headers: authHeaders() }
        );
    } catch {
        // Intentionally silent.
    }
};
